import {
  Box,
  Flex,
  Heading,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Tag,
  Text,
  useMediaQuery,
  useTheme,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/BackButton";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import QueryStateWrapper from "../../components/QueryStateWrapper";
import { useHiddenGemsLocations } from "../../data/hiddenGems.api";
import { useFocusOnPoints } from "../../hooks/useFocusOnPoints";

const HiddenGems = () => {
  const { data, isLoading, error } = useHiddenGemsLocations();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  // Match the wrapper's desktop cutoff so we only constrain height in the bottom-sheet variant
  const [desktop] = useMediaQuery("(min-width: 1100px)");

  const filtered = useMemo(() => {
    if (!data) return [];
    const withCoords = data.filter((loc) => loc.latitude && loc.longitude);
    if (!searchQuery.trim()) return withCoords;
    const q = searchQuery.toLowerCase();
    return withCoords.filter(
      (loc) =>
        loc.name.toLowerCase().includes(q) ||
        loc.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [data, searchQuery]);

  const points = useMemo(() => {
    return filtered.map((loc) => ({
      lat: loc.latitude!,
      lng: loc.longitude!,
      color: theme?.colors?.["lancsRed"]?.["300"],
      onClick: () => navigate(`/hidden-gems/${loc.key}`),
    }));
  }, [filtered, theme?.colors, navigate]);

  useFocusOnPoints(points);

  return (
    <HybridBottomSheetDrawer>
      <BackButton />

      <Box p={6}>
        <Heading size="md" mb={1}>
          Hidden Gems
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={4}>
          Discover recommended spaces across campus
        </Text>

        <InputGroup mb={4}>
          <InputLeftElement pointerEvents="none">
            <FaSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Search hidden gems..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            borderRadius="lg"
          />
        </InputGroup>

        <Text fontSize="xs" color="gray.400" mb={3}>
          {filtered.length} location{filtered.length !== 1 ? "s" : ""}
        </Text>

        <QueryStateWrapper isLoading={isLoading} error={error} data={data}>
          <Stack
            spacing={3}
            maxH={desktop ? undefined : "60vh"}
            overflowY={desktop ? undefined : "auto"}
            // Stop the sheet's drag-to-resize gesture from hijacking inner-list scroll on mobile
            sx={desktop ? undefined : { overscrollBehavior: "contain" }}
          >
            {filtered.map((loc) => (
              <Box
                key={loc.key}
                borderWidth={1}
                borderRadius="lg"
                cursor="pointer"
                overflow="hidden"
                _hover={{ bg: "whiteAlpha.100" }}
                onClick={() => navigate(`/hidden-gems/${loc.key}`)}
              >
                {loc.imageUrl && (
                  <Image
                    src={loc.imageUrl}
                    objectFit="cover"
                    h={24}
                    w="full"
                    fallback={<Box h={0} />}
                  />
                )}
                <Box p={4}>
                  <Text fontWeight="bold">{loc.name}</Text>
                  {loc.buildingName && (
                    <Text fontSize="sm" color="gray.500">{loc.buildingName}</Text>
                  )}
                  {loc.tags.length > 0 && (
                    <Flex flexWrap="wrap" gap={1} mt={2}>
                      {loc.tags.slice(0, 5).map((tag) => (
                        <Tag key={tag} size="sm" colorScheme="red" variant="subtle">
                          {tag}
                        </Tag>
                      ))}
                    </Flex>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </QueryStateWrapper>
      </Box>
    </HybridBottomSheetDrawer>
  );
};

export default HiddenGems;
